import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChefHat, ExternalLink, Hotel, Building2, Users, ShieldCheck, Flame, Sparkles, UtensilsCrossed, MessageCircle } from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";

const SWIGGY_URL = "https://www.swiggy.com/city/jaipur/nok-chhonk-vaishali-nagar-rest1451281";
const ZOMATO_URL = "https://www.zomato.com/jaipur/nok-chhonk-vaishali-nagar";
const WHATSAPP_NUMBER = "917357056609";
const CHEF_IMG = "https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?auto=format&fit=crop&w=1400&q=80";
const THALI_IMG = "https://images.unsplash.com/photo-1680993032090-1ef7ea9b51e5?auto=format&fit=crop&w=1400&q=80";

const THALIS = [
  {
    tag: "Veg",
    price: "₹129 – ₹169",
    tone: "amber",
    copy: "Rice at the centre, a hearty Odia dalma, Bengali vegetarian classics and shukto, plus bhaja, salad, papad and a rotating sweet. Complete, generous, homestyle.",
  },
  {
    tag: "Non-Veg",
    price: "₹280 – ₹309",
    tone: "flame",
    copy: "Kosha-style chicken or the fish curry of the day over steamed rice, with rich sides, bhaja and dessert — the Sunday spread, any day of the week.",
  },
];

const MENU = [
  {
    icon: UtensilsCrossed,
    title: "The everyday thali",
    copy: "Built around Odia dalma and Bengali kosha traditions — dal, seasonal vegetables, rice and sides that change with the market, not the franchise calendar.",
  },
  {
    icon: Sparkles,
    title: "The weekly rotation",
    copy: "Pakhala bhata for Jaipur summers, luchi-alur dom on festive mornings, shukto and ghanto through the week — a rotating calendar of Odia & Bengali specials.",
  },
  {
    icon: Flame,
    title: "Sweets & sides",
    copy: "Chhena poda and mishti for the quiet endings, fried sides and pickles for the loud beginnings. The parts of the plate people remember.",
  },
];

const B2B = [
  { icon: Hotel, title: "Hotels", copy: "Consistent, hygienic staff meals under per-plate contracts." },
  { icon: Users, title: "PGs & Hostels", copy: "Daily mess tiffins — lunch and dinner rotations residents actually finish." },
  { icon: Building2, title: "Offices", copy: "Team-lunch subscriptions with weekly rotating regional menus." },
];

export default function NokChhonk() {
  useEffect(() => {
    document.title = "Nok Chhonk — Odia & Bengali Cloud Kitchen, Jaipur | TOSIASI Creatives";
  }, []);

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-40 pb-20 md:pt-48">
        <div className="glow-amber pointer-events-none absolute -left-40 top-0 h-[560px] w-[560px]" />
        <div className="glow-amber pointer-events-none absolute -right-40 top-64 h-[480px] w-[480px] opacity-60" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="eyebrow"
            >
              A TOSIASI Creatives venture — now live
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: EASE }}
              className="mt-8"
            >
              <div className="inline-block rounded-3xl bg-white/95 p-5 shadow-[0_24px_90px_rgba(245,158,11,0.18)] md:p-7">
                <img
                  src="/nokchhonk-logo.jpeg"
                  alt="Nok Chhonk — Odia & Bengali cloud kitchen"
                  data-testid="nokchhonk-logo"
                  className="h-28 w-auto object-contain md:h-36"
                />
              </div>
            </motion.div>
            <h1 className="sr-only">Nok Chhonk — Odia &amp; Bengali cloud kitchen, Jaipur</h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
              className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
            >
              Authentic Odia &amp; Bengali regional comfort food — cooked fresh in Vaishali Nagar, Jaipur, and
              delivered hot to your door.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-8 flex flex-wrap gap-2"
            >
              {["FSSAI Licensed Kitchen", "Live on Swiggy", "Live on Zomato"].map((b) => (
                <span
                  key={b}
                  className="rounded-full border border-amber-400/25 bg-amber-400/[0.07] px-4 py-1.5 text-xs text-amber-300"
                >
                  {b}
                </span>
              ))}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: EASE }}
            className="lg:col-span-5"
          >
            <div className="relative">
              <img
                src={THALI_IMG}
                alt="Nok Chhonk thali"
                className="h-72 w-full rounded-3xl border border-white/10 object-cover shadow-[0_40px_120px_rgba(245,158,11,0.12)] md:h-96"
              />
              <div className="glass-strong absolute -bottom-5 left-6 flex items-center gap-3 rounded-2xl px-5 py-3.5">
                <ChefHat size={18} className="text-amber-400" />
                <p className="text-xs text-slate-300">Cooked to order · Vaishali Nagar, Jaipur</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ORDER BUTTONS */}
      <section className="py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="glass relative overflow-hidden rounded-3xl p-8 md:p-10">
              <div className="glow-amber pointer-events-none absolute -left-20 -top-20 h-64 w-64 opacity-80" />
              <div className="relative flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
                <div>
                  <h2 className="font-display text-2xl font-semibold text-white md:text-3xl">Order tonight&apos;s thali</h2>
                  <p className="mt-2 max-w-md text-sm text-slate-400">
                    Live menus, real-time pricing and delivery zones on both platforms.
                  </p>
                </div>
                <div className="flex flex-col items-start gap-3">
                  <div className="flex flex-wrap gap-4">
                    <a
                      href={SWIGGY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="order-swiggy-button"
                      className="btn btn-amber"
                    >
                      Order on Swiggy <ExternalLink size={16} />
                    </a>
                    <a
                      href={ZOMATO_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="order-zomato-button"
                      className="btn btn-outline-amber"
                    >
                      Order on Zomato <ExternalLink size={16} />
                    </a>
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Nok Chhonk! I'd like to order today's thali.")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="order-whatsapp-button"
                      className="btn btn-whatsapp"
                    >
                      <MessageCircle size={16} /> Order on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THALI PRICING */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">Thali pricing</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
              Two thalis. <span className="font-light text-slate-400">Zero shortcuts.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {THALIS.map((t, i) => (
              <Reveal key={t.tag} delay={0.08 * i}>
                <div
                  className="glass card-hover relative h-full overflow-hidden rounded-3xl p-10"
                  data-testid={`thali-card-${t.tag.toLowerCase().replace("-", "")}`}
                >
                  <div className="glow-amber pointer-events-none absolute -right-24 -top-24 h-72 w-72 opacity-70" />
                  <span
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${
                      t.tone === "flame"
                        ? "border-orange-400/30 bg-orange-400/10 text-orange-300"
                        : "border-amber-400/30 bg-amber-400/10 text-amber-300"
                    }`}
                  >
                    <Flame size={12} /> {t.tag}
                  </span>
                  <p className="mt-8 font-display text-4xl font-semibold text-white md:text-5xl">{t.price}</p>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Price band · varies by day&apos;s menu
                  </p>
                  <p className="mt-6 text-sm leading-relaxed text-slate-400">{t.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-6 text-xs text-slate-500">
              Full itemised menu and live pricing on Swiggy &amp; Zomato.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MENU OVERVIEW */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">The food</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
              Odia comfort, Bengali soul, <span className="font-light text-slate-400">Jaipur address.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {MENU.map((m, i) => (
              <Reveal key={m.title} delay={0.08 * i}>
                <div className="glass card-hover h-full rounded-3xl p-8" data-testid={`menu-overview-${i}`}>
                  <m.icon size={22} className="text-amber-400" />
                  <h3 className="mt-6 font-display text-xl font-semibold text-white">{m.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{m.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* KITCHEN STRIP */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-white/10">
              <img src={CHEF_IMG} alt="Nok Chhonk kitchen" className="h-72 w-full object-cover md:h-80" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#080C14]/95 via-[#080C14]/70 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-14">
                <ShieldCheck size={24} className="text-amber-400" />
                <h3 className="mt-5 max-w-md font-display text-2xl font-semibold text-white md:text-3xl">
                  A licensed kitchen, run like a production line
                </h3>
                <p className="mt-3 max-w-sm text-sm text-slate-400">
                  Hygiene-first operations, licensed under FSSAI, delivering across Jaipur through Swiggy &amp;
                  Zomato.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* B2B */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow">B2B — mess &amp; tiffin contracts</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-semibold tracking-tight text-white md:text-4xl">
              Feed your people <span className="font-light text-slate-400">properly.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {B2B.map((b, i) => (
              <Reveal key={b.title} delay={0.08 * i}>
                <div className="glass card-hover h-full rounded-3xl p-8" data-testid={`b2b-card-${b.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <b.icon size={22} className="text-amber-400" />
                  <h3 className="mt-6 font-display text-xl font-semibold text-white">{b.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{b.copy}</p>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/80">
                    Custom per-head pricing
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/contact?inquiry=nok-chhonk-b2b" data-testid="b2b-quote-cta" className="btn btn-amber">
                Request a B2B quote <ArrowRight size={16} />
              </Link>
              <p className="text-xs text-slate-500">Contracts from 30 meals a day upwards.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
